import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { DutyCode, DutyHourType } from 'generated';
import { omit } from 'lodash';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import getEditDutyCodeSchema from '../utils/editDutyCodeSchema';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import ValidFromValidTo from 'components/shared/ControlledFields/ControlledValidFromValidTo';
import DutyCodeBaseFields from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { Dialog, LoadingOverlay, showSnackbar } from 'components/shared/index';

import Actions from '../../Actions';

type Props = {
  isOpen: boolean;
  dutyCode: DutyCode;
  handleClose: () => void;
  onSave: () => void;
  organizationalUnitId?: number;
};

export type DutyCodePeriodForm = {
  id: number;
  dutyHourTypeId: number;
  startTime: Date;
  endTime: Date;
  dutyHourType: DutyHourType;
};

const schema = getEditDutyCodeSchema();
export type DutyCodeForm = InferType<typeof schema>;

function EditDutyCode({
  isOpen,
  dutyCode,
  handleClose,
  onSave,
  organizationalUnitId
}: Props) {
  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeForm>({
    resolver: yupResolver(getEditDutyCodeSchema())
  });

  const { control, handleSubmit, reset } = methods;

  const { mutate: updateDutyCode, isLoading: isUpdating } =
    mutations.useUpdateDutyCode();

  const QueryClient = useQueryClient();

  function handleReset(dutyCode: DutyCode) {
    reset({
      ...omit(dutyCode, ['validTo', 'validFrom', 'periods']),
      displayDutyCode: dutyCode.code,
      validFrom: new Date(dutyCode.validFrom || ''),
      validTo: new Date(dutyCode.validTo || '')
    });
  }

  useEffect(() => {
    handleReset(dutyCode);
  }, [dutyCode]);

  function onSubmit(data: DutyCodeForm) {
    updateDutyCode(
      {
        ...omit(dutyCode, ['dutyCode', 'colorCode', 'validTo']),
        code: data.displayDutyCode,
        colorCode: data.colorCode,
        validFrom: data.validFrom.toISOString(),
        validTo: data.validTo.toISOString(),
        organizationalUnitId: organizationalUnitId,
        dutyCodeId: dutyCode.dutyCodeId
      },
      {
        onSuccess: () => {
          QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
          onSave();
        },
        onError: (error: any) => {
          const message = t(
            error?.response?.data?.error?.message || 'somethingWentWrong'
          );
          showSnackbar({
            severity: 'error',
            message: message
          });
        }
      }
    );
  }

  function onDialogClose() {
    handleClose();
  }

  return (
    <Dialog
      open={isOpen}
      title={dutyCode ? t('editDutyCode') : t('addDutyCode')}
      handleClose={onDialogClose}
      PaperProps={{
        sx: {
          minWidth: 550
        }
      }}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <DutyCodeBaseFields />
          <ValidFromValidTo control={control} />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isUpdating} />
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default EditDutyCode;
